import type { ComponentPropsWithoutRef, JSX, ReactNode } from 'react';

import { AlertIcon } from '../assets/icons/AlertIcon.js';
import { classNames } from '../lib/classNames.js';
import * as styles from './Alert.css.js';

export type AlertKind = keyof typeof styles.kind;

export type AlertProps = Omit<ComponentPropsWithoutRef<'div'>, 'title'> & {
  readonly action?: ReactNode;
  readonly kind?: AlertKind;
  readonly variant?: 'default' | 'inline';
  readonly title?: ReactNode;
};

export function Alert({
  action,
  children,
  className,
  kind = 'info',
  variant = 'default',
  title,
  ...props
}: AlertProps): JSX.Element {
  const isError = kind === 'error';

  return (
    <div
      {...props}
      aria-live={props['aria-live'] ?? (isError ? 'assertive' : 'polite')}
      className={classNames(styles.alert, styles.kind[kind], styles.variant[variant], className)}
      data-slot="alert"
      data-kind={kind}
      data-variant={variant}
      role={props.role ?? (isError ? 'alert' : 'status')}
    >
      <div className={styles.body}>
        {kind === 'info' ? null : (
          <span className={styles.icon} data-alert-icon={kind}>
            <AlertIcon kind={kind} className={styles.iconGraphic} />
          </span>
        )}
        <div className={styles.content}>
          {title ? <strong className={styles.title}>{title}</strong> : null}
          <div className={styles.description}>{children}</div>
        </div>
      </div>
      {action ? <div className={styles.action}>{action}</div> : null}
    </div>
  );
}
